import {
    HOST_TOOL_REFUSED_TEXT,
    HOST_TOOL_SOURCE,
    hostToolNoAnswerText,
    isReservedHostToolName,
    type HostToolDefinition,
    type HostToolResult,
    type HostToolsCapability,
} from 'models'

/**
 * A tool the host registered, in the shape of WebMCP's
 * `navigator.modelContext.registerTool`: the MCP definition plus the function
 * that runs it (spec `host-tools.md`).
 */
export type RegisteredHostTool = HostToolDefinition & {
    execute: (
        input: unknown,
        options: { signal: AbortSignal; callId: string; messageId: string },
    ) => Promise<HostToolResult | HostToolDeferredResult>
    /**
     * A tool the widget itself provides (the page tools of spec
     * `page-tools.md`) carries its own policy, and does not depend on the
     * agent's host tool settings.
     */
    policy?: { timeoutMs: number }
    /**
     * Decides per call whether the user confirms first: the question to ask
     * (or the whole card), or nothing. On the widget's own tools (with a
     * `policy`) it replaces the agent's rules for the tool. On a host's tool
     * it can only add a confirmation: the agent's rules still ask when the
     * question is nothing.
     */
    confirmation?: (input: unknown) => string | HostToolConfirmation | undefined
    /**
     * What a step of this call says it does, naming its target ("Clicking
     * “Create”"): the control bar and the chat show it while the page is open
     * (spec `page-tools-improvements.md`, M8). Never a value the call writes.
     */
    describe?: (input: unknown) => string | undefined
}

/**
 * The confirmation card of a call (spec `page-tools-improvements.md`, M2).
 * It stays in the browser: neither the details nor the question reach the
 * backend, the model or the saved chat.
 */
export type HostToolConfirmation = {
    question: string
    /**
     * Lines under the question. With `before` they are changes, shown as a
     * table of what each field was and becomes (a submit's fields the agent
     * changed); without, plain label and value.
     */
    details?: { label: string; value: string; before?: string }[]
    /** The fields a submit sends unchanged, folded under the details. */
    unchanged?: { label: string; value: string }[]
    /** What the model reads when the user says no, in place of the plain refusal. */
    refusal?: string
}

/**
 * A tool a page registers on the element, as the registry keeps it: the
 * public fields only. `policy` belongs to the widget's own tools, so a page
 * can neither lift the agent's timeout nor replace its confirmations. The
 * functions are called on the page's own object, so a tool written as a class
 * instance keeps its `this`.
 */
export const hostToolFromPage = (
    tool: RegisteredHostTool,
): RegisteredHostTool => ({
    name: tool.name,
    description: tool.description,
    inputSchema: tool.inputSchema,
    annotations: tool.annotations,
    execute: (input, options) => tool.execute(input, options),
    confirmation:
        typeof tool.confirmation === 'function'
            ? (input) => tool.confirmation?.(input)
            : undefined,
})

/**
 * A result whose effect must wait until the backend holds it: a click that
 * may unload the page runs only once the resume carrying this result has
 * reached the backend, or the page would leave before the answer could.
 */
export type HostToolDeferredResult = {
    result: HostToolResult
    afterOutput: () => void
    /**
     * Called instead of `afterOutput` when the effect will never run: the
     * resume did not reach the backend, or the user stopped the agent. What
     * the call held for later is dropped there.
     */
    abandon?: () => void
}

/**
 * Set by a page click that may unload the page, read by the widget on the
 * next page (spec `page-tools.md`): the answer that clicked goes on there.
 */
export const PAGE_NAVIGATION_KEY = 'agentstudio:page-navigation'

/**
 * Null-safe: a host `execute` may resolve anything, and whatever is not a
 * result must reach the backend as it is, to be refused there as "not a tool
 * result", not turn into a TypeError here.
 */
const isDeferred = (
    value: HostToolResult | HostToolDeferredResult,
): value is HostToolDeferredResult =>
    typeof (value as HostToolDeferredResult | null)?.afterOutput === 'function'

/** A host call as the stream announced it. */
export type HostToolCall = {
    toolCallId: string
    toolName: string
    input: unknown
    messageId: string
    readOnly: boolean
    provider?: 'app' | 'page'
}

type HostToolOutput =
    | { tool: string; toolCallId: string; output: HostToolResult }
    | {
          tool: string
          toolCallId: string
          state: 'output-error'
          errorText: string
      }

/** True for the `onToolCall` argument of a call the backend stamped as a host call. */
export const isHostToolCall = (toolCall: unknown): boolean =>
    (toolCall as { toolMetadata?: { source?: unknown } }).toolMetadata
        ?.source === HOST_TOOL_SOURCE

/**
 * The definitions a request declares: the registered tools the agent allows,
 * without their `execute`. Nothing when the agent has host tools off.
 */
export const declaredHostTools = (
    registry: readonly RegisteredHostTool[] | undefined,
    capability: HostToolsCapability | undefined,
): HostToolDefinition[] | undefined => {
    if (!capability || !registry?.length) {
        return undefined
    }
    const allowed = new Set(capability.allowed)
    const declared = registry
        .filter(
            (tool) =>
                allowed.has(tool.name) && !isReservedHostToolName(tool.name),
        )
        .map(
            ({
                execute: _execute,
                confirmation: _confirmation,
                policy: _policy,
                ...definition
            }) => definition,
        )
    return declared.length > 0 ? declared : undefined
}

/**
 * Runs host calls one at a time, in the order the stream announced them.
 *
 * `enqueue` returns at once: the SDK awaits `onToolCall` before it reads the
 * rest of the stream, and `addToolOutput` goes through the same job queue of
 * the `Chat`, so awaiting the host there would stall the stream and awaiting
 * the output would deadlock. One at a time because writes to a document depend
 * on their order, and a model can emit several calls in one step.
 *
 * `cancelAll` is for the stop, and for any message the user sends while a call
 * is pending: the request that announced the call has ended, so the chat looks
 * ready and the user can move on. It bumps the generation, so a result that
 * lands afterwards is dropped, and aborts the calls in flight. It adds no
 * output: an output after the stop would resume the turn through
 * `sendAutomaticallyWhen`. The backend closes those calls on the next message.
 *
 * `onPendingChange` reports the calls queued or running: while there are any
 * the chat is busy, whatever the status of its last request says.
 */
export function createHostToolRunner(deps: {
    registry: () => readonly RegisteredHostTool[] | undefined
    capability: () => HostToolsCapability | undefined
    addToolOutput: (output: HostToolOutput) => void
    /**
     * Asks the user. `confirmation` is the tool's own card (a page click
     * names what it clicks, a submit lists its fields); without one the card
     * asks about the call by its title.
     */
    confirm: (
        call: HostToolCall,
        confirmation?: HostToolConfirmation,
    ) => Promise<boolean>
    /** Resolves true once the resume that carries the last output reached the backend. */
    waitForResume: () => Promise<boolean>
    onPendingChange?: (pending: number) => void
    /** The call starting, or `undefined` when it ends: what the page control bar shows. */
    onActivity?: (call: HostToolCall | undefined) => void
}) {
    let generation = 0
    let pending = 0
    const setPending = (value: number) => {
        pending = value
        deps.onPendingChange?.(pending)
    }
    let tail: Promise<void> = Promise.resolve()
    const inFlight = new Map<string, AbortController>()
    /**
     * What answered calls still hold until their resume (a navigation of the
     * page): a stop lets it go at once, not when the wait for a resume that
     * will never come runs out.
     */
    const awaitingResume = new Set<() => void>()

    /** A result nobody will deliver: what it holds is let go. */
    const letGo = (result: HostToolResult | HostToolDeferredResult) => {
        if (isDeferred(result)) {
            result.abandon?.()
        }
    }

    const needsConfirmation = (
        tool: RegisteredHostTool,
        capability: HostToolsCapability,
    ): boolean =>
        capability.requireConfirmation.includes(tool.name) ||
        (capability.confirmDestructive &&
            tool.annotations?.destructiveHint === true)

    // Two closings and not one taking either: a host may resolve a string,
    // which is not a tool result and must reach the backend as one, to be
    // refused there, not be read as the error text of a write that landed.
    const answer = (call: HostToolCall, output: HostToolResult) =>
        deps.addToolOutput({
            tool: call.toolName,
            toolCallId: call.toolCallId,
            output,
        })
    const fail = (call: HostToolCall, errorText: string) =>
        deps.addToolOutput({
            tool: call.toolName,
            toolCallId: call.toolCallId,
            state: 'output-error',
            errorText,
        })

    async function run(call: HostToolCall, callGeneration: number) {
        const isCurrent = () => callGeneration === generation
        const capability = deps.capability()
        const tool = deps
            .registry()
            ?.find((candidate) => candidate.name === call.toolName)
        const timeoutMs = tool?.policy?.timeoutMs ?? capability?.timeoutMs
        if (!tool || timeoutMs === undefined) {
            fail(call, hostToolNoAnswerText(call.readOnly))
            return
        }
        let card: HostToolConfirmation | undefined
        try {
            const asked: unknown = tool.confirmation?.(call.input)
            // A page's tool may answer anything: a card only from a string
            // or from an object that has its question
            card =
                typeof asked === 'string'
                    ? { question: asked }
                    : typeof (asked as HostToolConfirmation | null)
                            ?.question === 'string'
                      ? (asked as HostToolConfirmation)
                      : undefined
        } catch {
            // Thrown here, the call would never get an answer and the turn
            // would wait on it: a call nobody could judge does not run.
            fail(call, 'The host app could not prepare this call.')
            return
        }
        const byAgentRules =
            capability !== undefined && needsConfirmation(tool, capability)
        const asks =
            tool.policy && tool.confirmation
                ? card !== undefined
                : card !== undefined || byAgentRules
        if (asks) {
            const confirmed = await deps.confirm(call, card)
            if (!isCurrent()) {
                return
            }
            if (!confirmed) {
                fail(call, card?.refusal ?? HOST_TOOL_REFUSED_TEXT)
                return
            }
        }

        const controller = new AbortController()
        inFlight.set(call.toolCallId, controller)
        deps.onActivity?.(call)
        const timer = setTimeout(
            () => controller.abort(new Error('timeout')),
            timeoutMs,
        )
        let running:
            Promise<HostToolResult | HostToolDeferredResult> | undefined
        try {
            const aborted = new Promise<never>((_, reject) => {
                controller.signal.addEventListener(
                    'abort',
                    () => reject(controller.signal.reason),
                    { once: true },
                )
            })
            running = Promise.resolve(
                tool.execute(call.input, {
                    signal: controller.signal,
                    callId: call.toolCallId,
                    messageId: call.messageId,
                }),
            )
            const result = await Promise.race([running, aborted])
            if (!isCurrent()) {
                letGo(result)
                return
            }
            if (isDeferred(result)) {
                answer(
                    call,
                    result.result ?? (null as unknown as HostToolResult),
                )
                let settled = false
                const abandon = () => {
                    if (!settled) {
                        settled = true
                        awaitingResume.delete(abandon)
                        result.abandon?.()
                    }
                }
                awaitingResume.add(abandon)
                // Not awaited: the queue moves on, and a stop in between
                // (generation bumped) cancels the effect.
                void deps.waitForResume().then((reached) => {
                    if (settled) {
                        return
                    }
                    if (reached && isCurrent()) {
                        settled = true
                        awaitingResume.delete(abandon)
                        result.afterOutput()
                    } else {
                        abandon()
                    }
                })
            } else {
                // `undefined` would vanish from the JSON of the resume and
                // leave the call open: `null` reaches the backend, which
                // refuses it as not a tool result.
                answer(call, result ?? (null as unknown as HostToolResult))
            }
        } catch (error) {
            // Given up before it answered (a stop, a timeout, another chat):
            // what it holds once it does has nobody to deliver it
            void running?.then(letGo, () => undefined)
            if (!isCurrent()) {
                return
            }
            // A timeout says nothing about a write: it may have landed.
            const timedOut = controller.signal.aborted
            fail(
                call,
                timedOut
                    ? hostToolNoAnswerText(call.readOnly)
                    : error instanceof Error && error.message
                      ? error.message
                      : 'The host app failed to run this call.',
            )
        } finally {
            clearTimeout(timer)
            inFlight.delete(call.toolCallId)
            deps.onActivity?.(undefined)
        }
    }

    return {
        enqueue(call: HostToolCall) {
            const callGeneration = generation
            setPending(pending + 1)
            tail = tail
                .then(() =>
                    callGeneration === generation
                        ? run(call, callGeneration)
                        : undefined,
                )
                .catch(() => undefined)
                .then(() => {
                    if (callGeneration === generation) {
                        setPending(pending - 1)
                    }
                })
        },
        cancelAll() {
            generation++
            setPending(0)
            for (const abandon of [...awaitingResume]) {
                abandon()
            }
            for (const controller of inFlight.values()) {
                controller.abort(new Error('cancelled'))
            }
            inFlight.clear()
        },
    }
}
